import { Accordion, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import { IconClipboardList } from "@tabler/icons-react";
import type { FC } from "react";
import { AccordionSection as Section } from "@/common-ui/accordion/AccordionSection";
import { UncheckedRadioIcon } from "@/sales-tools/home-product/components/Icons";
import { isValidHttpUrl } from "../utils";

const resourcesContent = [
    {
        sectionTitle: "Call Checklist",
        sectionGroups: [
            {
                heading: "Connect with the caller",
                bulletPoints: [
                    "Recognize their concerns, set expectations, and even acknowledge their device brand and Verizon tenure.",
                    "Ask open and closed-ended questions to understand their experience.",
                    "Use tools and resources that lead to a resolution.",
                ],
            },
            {
                heading: "Provide a solution",
                bulletPoints: [
                    "Use tools and resources that lead to a resolution.",
                    "Address the customer’s concern when presenting your solution.",
                    "Personalize the coverage",
                ],
            },
        ],
    },
    {
        sectionTitle: "Useful Links",
        sectionGroups: [
            {
                heading: "Activation Fee FAQs - Verizon Care",
                bulletPoints: ["https://kbsolutohelix.wpengine.com/en/?post_type=s_article&p=363636&preview=true"],
            },
        ],
    },
    {
        sectionTitle: "Six Key Sales Behaviors",
        sectionGroups: [
            {
                heading: "Build customer rapport",
                bulletPoints: [
                    "Build customer rapport by actively listening and asking relatable questions, while helping with their issue.",
                ],
            },
            {
                heading: "Establish Credibility",
                bulletPoints: [
                    "Establish credibility by answering customers’ questions accurately, clearly, and confidently.",
                ],
            },
            {
                heading: "Position the product",
                bulletPoints: ["Position the product in a way that relates to the customer’s specific needs."],
            },
            {
                heading: "Lead with value and benefits",
                bulletPoints: [
                    "Lead with value and benefits rather than price, balancing protection and technical support.",
                ],
            },
            {
                heading: "Ask to Send the Link for Enrollment",
                bulletPoints: [
                    "Can I text you a link to review important plan information and complete your enrollment?",
                ],
            },
            {
                heading: "Resolve any concerns",
                bulletPoints: [
                    "Start by asking ‘Why?’ to understand their perspective. Validate their feelings and mention that others have shared similar thoughts. If they decline after two respectful explanations, graciously thank them and move on.",
                ],
            },
        ],
    },
    {
        sectionTitle: "Quality Sales Verbatim",
        sectionGroups: [
            {
                heading: "Transition statement",
                bulletPoints: ["‘A separate coverage from your phone insurance’"],
            },
            {
                heading: "Price & Billing Transparency",
                bulletPoints: [
                    "This coverage is billed to your account, and charges begin upon enrollment. Your next bill will include the full $25 plus tax monthly amount plus prorated charges from this month. After that, you’ll just see the monthly fee.",
                ],
            },
            {
                heading: "Ask to Send the Link for Enrollment",
                bulletPoints: [
                    "Can I text you a link to review important plan information and complete your enrollment?",
                ],
            },
            {
                heading: "Disclosures",
                bulletPoints: [
                    "I’m going to play you a short recording with important plan disclosures. Let’s stay on the line together, and then I’ll finish your enrollment. (Play Full Disclosures Verbatim)",
                ],
            },
            {
                heading: "Sincere Sign-off",
                bulletPoints: ["You will receive a welcome email that includes a full set of Terms and Conditions."],
            },
        ],
    },
    {
        sectionTitle: "Activation Steps",
        sectionGroups: [
            {
                heading: "Go to Helix Search and enter keyword ‘activation’.",
                bulletPoints: ["https://helix.search.asurion53.com/"],
            },
            {
                heading: "Link to Ozmo:",
                bulletPoints: ["https://asurion-support.sds.modeaondemand.com/agents/en/"],
            },
            {
                heading: "Link to ACSS simulator:",
                bulletPoints: ["https://welearn.int.asurion.com/sims/acss/#/prototype/1ai31j5e/screen/94440059"],
            },
            {
                heading: "Link to Expert Assist:",
                bulletPoints: ["https://voice-assist.remoteexpert.anywhereexpert.us/"],
            },
            {
                heading: "Activation info needed: IMEI & ICCID (SIM #)",
                bulletPoints: [
                    "NOTE: Phones with eSIMs will not show SIM information until activation is complete and one is downloaded from the cellular network.",
                ],
            },
            {
                heading: "Follow the Manual Activation process if needed for training.",
                bulletPoints: ["NOTE: On real calls you should always try pending orders activation first."],
            },
            {
                heading: "Must be in an area with good cellular service.",
            },
            {
                heading: "Phone setup info needed:",
                bulletPoints: ["Account id and password (google account or apple account)."],
            },
        ],
    },
];

export const Sidebar: FC = () => {
    return (
        <Paper
            display="flex"
            shadow="md"
            radius="md"
            w="100%"
            h="100%"
            style={{
                background: "var(--mantine-color-dark-5)",
                flexDirection: "column",
            }}
        >
            <Group align="center" gap={8} py="1rem" px="1.25rem" c={"var(--mantine-color-dark-2)"}>
                <IconClipboardList size={20} />
                <Text fz="1rem" fw={700} tt="uppercase">
                    Resources
                </Text>
            </Group>
            <ScrollArea offsetScrollbars scrollbarSize={10} type="hover">
                <Accordion
                    defaultValue="Call Checklist"
                    chevronPosition="right"
                    styles={{
                        chevron: {
                            color: "var(--mantine-color-success-4)",
                            scale: 1.2,
                        },
                        control: {
                            color: "var(--mantine-color-dark-2)",
                            padding: "1rem 1.5rem",
                        },
                        content: {
                            fontSize: "0.875rem",
                            lineHeight: "1rem",
                            padding: "0 1.5rem 1rem",
                        },
                        label: {
                            fontSize: "1rem",
                            lineHeight: "1.25rem",
                            padding: 0,
                            fontWeight: 700,
                        },
                        item: {
                            width: "100%",
                            background: "transparent",
                            border: 0,
                            borderBottom: "1px solid var(--mantine-color-dark-4)",
                        },
                    }}
                >
                    {resourcesContent.map((resource) => (
                        <Section title={resource.sectionTitle} key={resource.sectionTitle}>
                            <Accordion.Panel>
                                {resource.sectionGroups.map((resourceGrouping) => (
                                    <Stack pt="0.5rem" gap="5px" key={resourceGrouping.heading}>
                                        <Text fz="1rem" lh="1.25rem" c="white" mb="0.5rem">
                                            {resourceGrouping.heading}
                                        </Text>
                                        <div style={{ marginBottom: "0.625rem" }}>
                                            {resourceGrouping.bulletPoints?.map((text) =>
                                                text ? (
                                                    <Group
                                                        gap="5px"
                                                        wrap="nowrap"
                                                        justify="top"
                                                        align="top"
                                                        px="0.25rem"
                                                        py="0.5rem"
                                                        key={text}
                                                    >
                                                        <div style={{ paddingTop: 4 }}>
                                                            <UncheckedRadioIcon />
                                                        </div>
                                                        <Text fz="0.875rem" lh="1.5rem" c="var(--mantine-color-dark-0)">
                                                            {isValidHttpUrl(text) ? (
                                                                <a
                                                                    href={text}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {text}
                                                                </a>
                                                            ) : (
                                                                text
                                                            )}
                                                        </Text>
                                                    </Group>
                                                ) : null,
                                            )}
                                        </div>
                                    </Stack>
                                ))}
                            </Accordion.Panel>
                        </Section>
                    ))}
                </Accordion>
            </ScrollArea>
        </Paper>
    );
};

export default Sidebar;
